import { Injectable } from '@angular/core';
import { ActionBackOfficeResponse } from '../../models/api.model';
import { PagedTableResponse } from '../../models/common/paged-table.model';
import {
  DiscountFreeRangeLogRow,
  DiscountFreeRangeRow,
  ImportDiscountFreeRangeItemStatus,
  ImportDiscountFreeRangeResult,
} from '../../models/parking/discount-free-range.model';

@Injectable({ providedIn: 'root' })
export class MapperDiscountFreeRangeService {
  mapRow(dto: unknown): DiscountFreeRangeRow {
    const r = (dto ?? {}) as Record<string, unknown>;
    return {
      id: Number(r['id'] ?? r['Id'] ?? 0),
      name: String(r['name'] ?? r['Name'] ?? ''),
      taiwanUuid: String(r['taiwanUuid'] ?? r['TaiwanUuid'] ?? ''),
      siteId: Number(r['siteId'] ?? r['SiteId'] ?? 0),
      siteName: String(r['siteName'] ?? r['SiteName'] ?? ''),
      licensePlate: String(r['licensePlate'] ?? r['LicensePlate'] ?? ''),
      startDate: (r['startDate'] ?? r['StartDate'] ?? null) as string | null,
      endDate: (r['endDate'] ?? r['EndDate'] ?? null) as string | null,
      isEnabled: !!(r['isEnabled'] ?? r['IsEnabled'] ?? false),
      remark: String(r['remark'] ?? r['Remark'] ?? ''),
      taiwanSyncStatus: String(r['taiwanSyncStatus'] ?? r['TaiwanSyncStatus'] ?? ''),
      taiwanLastSyncAt: (r['taiwanLastSyncAt'] ?? r['TaiwanLastSyncAt'] ?? null) as string | null,
      taiwanLastSyncError: String(r['taiwanLastSyncError'] ?? r['TaiwanLastSyncError'] ?? ''),
      canEdit: !!(r['canEdit'] ?? r['CanEdit'] ?? false),
      requestUid: (r['requestUid'] ?? r['RequestUid'] ?? null) as number | null,
      requestDisplayName: String(r['requestDisplayName'] ?? r['RequestDisplayName'] ?? ''),
      active: r['active'] != null || r['Active'] != null ? !!(r['active'] ?? r['Active']) : true,
      createDate: (r['createDate'] ?? r['CreateDate'] ?? null) as string | null,
    };
  }

  mapTable(res: unknown): PagedTableResponse<DiscountFreeRangeRow> {
    const raw = (res ?? {}) as Record<string, unknown>;
    const list = (raw['data'] ?? raw['Data'] ?? []) as unknown[];
    const pageSize = Number(raw['pageSize'] ?? raw['PageSize'] ?? 20);
    const total = Number(raw['total'] ?? raw['Total'] ?? 0);
    const page = Number(raw['page'] ?? raw['Page'] ?? 1);
    return {
      data: list.map((item) => this.mapRow(item)),
      total,
      page,
      pageSize,
      totalPages: Number(raw['totalPages'] ?? raw['TotalPages'] ?? Math.ceil(total / Math.max(pageSize, 1))),
    };
  }

  mapLogs(res: unknown): PagedTableResponse<DiscountFreeRangeLogRow> {
    const raw = (res ?? {}) as Record<string, unknown>;
    const list = (raw['data'] ?? raw['Data'] ?? []) as unknown[];
    const pageSize = Number(raw['pageSize'] ?? raw['PageSize'] ?? 20);
    const total = Number(raw['total'] ?? raw['Total'] ?? 0);
    const page = Number(raw['page'] ?? raw['Page'] ?? 1);
    return {
      data: list.map((item) => {
        const r = (item ?? {}) as Record<string, unknown>;
        return {
          id: Number(r['id'] ?? r['Id'] ?? 0),
          syncAction: String(r['syncAction'] ?? r['SyncAction'] ?? ''),
          status: String(r['status'] ?? r['Status'] ?? ''),
          httpStatusCode: (r['httpStatusCode'] ?? r['HttpStatusCode'] ?? null) as number | null,
          errorCode: String(r['errorCode'] ?? r['ErrorCode'] ?? ''),
          errorMessage: String(r['errorMessage'] ?? r['ErrorMessage'] ?? ''),
          durationMs: (r['durationMs'] ?? r['DurationMs'] ?? null) as number | null,
          createDate: (r['createDate'] ?? r['CreateDate'] ?? null) as string | null,
        };
      }),
      total,
      page,
      pageSize,
      totalPages: Number(raw['totalPages'] ?? raw['TotalPages'] ?? Math.ceil(total / Math.max(pageSize, 1))),
    };
  }

  mapActionResponse(res: unknown): ActionBackOfficeResponse {
    const raw = (res ?? {}) as Record<string, unknown>;
    return {
      isSuccess: Boolean(raw['isSuccess'] ?? raw['IsSuccess'] ?? false),
      message: String(raw['message'] ?? raw['Message'] ?? ''),
      messageEn: String(raw['messageEn'] ?? raw['MessageEn'] ?? ''),
      code: String(raw['code'] ?? raw['Code'] ?? ''),
      IdReturn: Number(raw['IdReturn'] ?? raw['idReturn'] ?? 0),
    };
  }

  toYmd(value: Date | string | null | undefined): string {
    if (!value) return '';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  parseDate(value: string | null | undefined): Date | null {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  formatDisplay(value: string | null | undefined): string {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    const day = String(d.getDate()).padStart(2, '0');
    const m = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}/${m}/${d.getFullYear()}`;
  }

  syncStatusLabel(status: string): string {
    const map: Record<string, string> = {
      not_sent: 'ยังไม่ส่ง',
      pending: 'กำลังส่ง',
      success: 'สำเร็จ',
      failed: 'ล้มเหลว',
    };
    return map[status] ?? (status || '—');
  }

  // --- วันที่ + เวลา (ค.ศ., 24 ชม.) ---

  /** "8" / "830" / "0830" / "8:30" / "08.30" → "08:30" · ไม่ถูกต้อง → null */
  normalizeHm(value: string | null | undefined): string | null {
    const raw = (value ?? '').trim().replace(/[.\s]/g, ':');
    if (!raw) return null;
    let h: number;
    let m: number;
    if (raw.includes(':')) {
      const [hs, ms = '0'] = raw.split(':');
      h = Number(hs);
      m = Number(ms);
    } else if (/^\d{1,2}$/.test(raw)) {
      h = Number(raw);
      m = 0;
    } else if (/^\d{3,4}$/.test(raw)) {
      h = Number(raw.slice(0, raw.length - 2));
      m = Number(raw.slice(-2));
    } else {
      return null;
    }
    if (!Number.isInteger(h) || !Number.isInteger(m) || h < 0 || h > 23 || m < 0 || m > 59) return null;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  toHm(value: Date | string | null | undefined): string {
    if (!value) return '';
    const d = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }

  /** รวมวันที่ (Date) + เวลา HH:mm → Date · ไม่ครบ → null */
  combineDateTime(date: Date | null, hm: string | null | undefined): Date | null {
    const time = this.normalizeHm(hm);
    if (!date || !time) return null;
    const [h, m] = time.split(':').map(Number);
    const d = new Date(date);
    d.setHours(h, m, 0, 0);
    return d;
  }

  /** Date → "yyyy-MM-dd HH:mm" สำหรับ API */
  toYmdHm(value: Date | null): string {
    if (!value) return '';
    return `${this.toYmd(value)} ${this.toHm(value)}`;
  }

  /** "DD/MM/YYYY HH:mm" */
  formatDisplayDateTime(value: string | null | undefined): string {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return `${this.formatDisplay(value)} ${this.toHm(d)}`;
  }

  /** ทะเบียนชิดกัน — ตัดช่องว่างทุกชนิด (ตรงกับ API) */
  compactPlate(value: unknown): string {
    return String(value ?? '').replace(/[\s\u00A0\u200B]+/g, '');
  }

  mapImportResult(res: unknown): ImportDiscountFreeRangeResult {
    const raw = (res ?? {}) as Record<string, unknown>;
    const list = (raw['items'] ?? raw['Items'] ?? []) as unknown[];
    return {
      isSuccess: Boolean(raw['isSuccess'] ?? raw['IsSuccess'] ?? false),
      message: String(raw['message'] ?? raw['Message'] ?? ''),
      total: Number(raw['total'] ?? raw['Total'] ?? 0),
      successCount: Number(raw['successCount'] ?? raw['SuccessCount'] ?? 0),
      skippedCount: Number(raw['skippedCount'] ?? raw['SkippedCount'] ?? 0),
      failedCount: Number(raw['failedCount'] ?? raw['FailedCount'] ?? 0),
      items: list.map((item) => {
        const r = (item ?? {}) as Record<string, unknown>;
        return {
          rawLicensePlate: String(r['rawLicensePlate'] ?? r['RawLicensePlate'] ?? ''),
          licensePlate: String(r['licensePlate'] ?? r['LicensePlate'] ?? ''),
          status: String(r['status'] ?? r['Status'] ?? 'failed') as ImportDiscountFreeRangeItemStatus,
          message: String(r['message'] ?? r['Message'] ?? ''),
          freeRangeId: (r['freeRangeId'] ?? r['FreeRangeId'] ?? null) as number | null,
        };
      }),
    };
  }
}
